import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, toast } from '@node-space/ui-components'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { REWARDS_REFERENCE_PREFIX } from 'pages/Rewards/constants/const'
import { useRewardsSheetContext } from 'pages/Rewards/context/RewardsSheetProvider'
import { useCreatePortfolio } from 'pages/Rewards/hooks/api/useCreatePortfolio'
import { useDepositPortfolio } from 'pages/Rewards/hooks/api/useDepositPortfolio'
import { useRewardsTracking } from 'pages/Rewards/hooks/useRewardsTracking'
import { RewardsDepositFormData } from 'pages/Rewards/types'
import { invalidateRewardsQueries, prepareDepositReviewData } from 'pages/Rewards/utils'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'
import { generateReference } from 'utils/utils'
import { RewardsAmountInput } from './RewardsAmountInput'
import { RewardsCurrencySelect } from './RewardsCurrencySelect'
import { RewardsReview } from './RewardsReview'
import { RewardsTermsCheckbox } from './RewardsTermsCheckbox'
import { RewardsWalletSelect } from './RewardsWalletSelect'
import { getDepositValidationSchema } from './validationSchema'

type DepositStep = 'form' | 'review'

export const RewardsDeposit = () => {
  const { t } = useTranslation()
  const { requestError, setRequestError } = useMappedErrorMessage()
  const { trackFormInitiate, trackFormCancel } = useRewardsTracking()
  const reactQueryClient = useQueryClient()

  const {
    userPortfolios,
    rewardsSheetAction,
    rewardsProducts,
    selectedCurrency: portfolioCurrency,
    handleSheetUpdate,
  } = useRewardsSheetContext()
  const { selectedCurrency: selectedCurrencyFromAction } = rewardsSheetAction || {}
  const [maxAmount, setMaxAmount] = useState('')
  const [currentStep, setCurrentStep] = useState<DepositStep>('form')

  const { depositToPortfolio, isDepositingToPortfolioPending } = useDepositPortfolio({
    onSuccess: data => {
      const walletReference = userPortfolios.find(
        portfolio => portfolio.currency === data.currency
      )?.walletId

      invalidateRewardsQueries(reactQueryClient, walletReference)
      rewardsSheetAction?.onClose?.()
      toast({
        variant: 'success',
        header: t('rewards.deposit.successNotificationTitle', {
          currency: `${data.amount} ${data.currency}`,
        }),
      })
    },
    onError: error => {
      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage: t('rewards.errors.depositToPortfolio'),
      })
    },
  })
  const { createPortfolio, isCreatingPortfolioPending } = useCreatePortfolio({
    onError: error => {
      const errorDescription = error?.message || error?.data?.message
      const mainMessage = t('rewards.errors.createPortfolio')
      const handledMessage = errorDescription ? (
        <Box flexCol gap={8}>
          <span>{mainMessage}</span>
          <pre className="text-body-regular-small">Error: {errorDescription}</pre>
        </Box>
      ) : (
        mainMessage
      )

      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage,
      })
    },
    onSuccess: () => {
      reactQueryClient.invalidateQueries({
        queryKey: reactQueryKeys.rewardsPortfolios(API_PAGINATION_OFFSET, API_PAGINATION_LIMIT),
      })
    },
  })

  const isPending = isCreatingPortfolioPending || isDepositingToPortfolioPending

  const formMethods = useForm<RewardsDepositFormData>({
    resolver: yupResolver(getDepositValidationSchema(t, maxAmount)),
    mode: 'onChange',
    defaultValues: {
      currency: portfolioCurrency || selectedCurrencyFromAction || '',
      allocateFrom: null,
      amount: '',
      termsAccepted: false,
    },
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch,
  } = formMethods

  const selectedCurrency = watch('currency')

  useEffect(() => {
    trackFormInitiate('allocate')
  }, [])

  useEffect(() => {
    if (selectedCurrency) {
      setValue('allocateFrom', null)
      setValue('amount', '')
    }
  }, [selectedCurrency, setValue])

  useEffect(() => {
    if (rewardsSheetAction) {
      if (currentStep === 'review') {
        handleSheetUpdate({
          header: t('rewards.deposit.reviewHeader'),
          onBack: onReviewBack,
        })
      } else {
        handleSheetUpdate({
          header: t('rewards.deposit.header'),
          onBack: undefined,
        })
      }
    }
  }, [currentStep, t, handleSheetUpdate, rewardsSheetAction])

  const onCancel = () => {
    trackFormCancel('allocate')
    rewardsSheetAction?.onClose?.()
  }

  const onReviewBack = () => {
    setCurrentStep('form')
  }

  const onSubmit = () => {
    setRequestError(null)
    setCurrentStep('review')
  }

  const onConfirmDeposit = async () => {
    const data = formMethods.getValues()
    setRequestError(null)

    const currency = data.currency
    const walletId = data.allocateFrom?.value
    const portfolioAssignedToCurrency = userPortfolios.find(
      portfolio => portfolio.currency === currency
    )
    let portfolioId = portfolioAssignedToCurrency?.id
    const earningProductId = rewardsProducts.find(product => product.currency === currency)?.id

    if (!earningProductId) {
      setRequestError({
        show: true,
        errorCode: 400,
        handledMessage: t('rewards.deposit.earningProductNotFound'),
      })
      return
    }

    if (!portfolioAssignedToCurrency) {
      try {
        const createdPortfolioData = await createPortfolio(earningProductId)
        portfolioId = createdPortfolioData?.id
      } catch (error) {
        // Error is already handled by useCreatePortfolio's onError callback
        return
      }
    }

    depositToPortfolio({
      reference: generateReference(REWARDS_REFERENCE_PREFIX),
      portfolioId,
      counterparty: {
        walletId,
        amount: data.amount,
      },
    })
  }

  if (currentStep === 'review') {
    return (
      <RewardsReview
        data={prepareDepositReviewData(formMethods.getValues(), selectedCurrency)}
        requestError={requestError}
        isPending={isPending}
        onCancel={onCancel}
        onConfirm={onConfirmDeposit}
      />
    )
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        tag="form"
        flexCol
        gapY={16}
        width="full"
        paddingY={24}
        onSubmit={handleSubmit(onSubmit)}
      >
        {requestError?.show && (
          <Callout variant="error" testId="deposit-error-callout">
            {requestError?.handledMessage}
          </Callout>
        )}

        {rewardsProducts.length > 1 && (
          <RewardsCurrencySelect
            filterByBalance="available"
            disabled={!!portfolioCurrency || isPending}
          />
        )}

        <RewardsWalletSelect
          selectedCurrency={selectedCurrency}
          name="allocateFrom"
          disabled={!selectedCurrency || isPending}
          testId="allocate-from-combobox"
          setMaxAmount={setMaxAmount}
        />

        <RewardsAmountInput
          selectedCurrency={selectedCurrency}
          maxAmount={maxAmount}
          disabled={!selectedCurrency || isPending}
        />

        <Callout variant="warning">{t('rewards.deposit.cooldownDescription')}</Callout>

        <RewardsTermsCheckbox disabled={isPending} />

        <Box flex justify="between" gapX={12} paddingT={24}>
          <Button onClick={onCancel} variant="secondary" type="button" testId="cancel-button">
            {t('cancel')}
          </Button>

          <Button
            variant="primary"
            type="submit"
            testId="allocate-button"
            disabled={isSubmitting || isPending}
            loading={isSubmitting || isPending}
          >
            {t('rewards.allocateCurrency')}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
